// User Types
export interface User {
	id: number;
	email: string;
	fullName?: string;
	firstName: string;
	lastName: string;
	phone?: string;
	address?: string;
	birthDate?: Date | string;
	photo?: string;
	avatar?: string;
	isActive: boolean;
	lastLogin?: Date;
	roleId: number;
	branchId?: number;
	/** Tenant company (dashboard); from JWT / profile after multi-tenant migration */
	companyId?: number;
	/** Tenant currency from login / `/auth/profile`; readable without `company.read`. */
	companyCurrency?: string | null;
	role?: Role;
	branch?: Branch;
	permissions?: string[];
	createdAt: Date;
	updatedAt: Date;
}

export interface Role {
	id: number;
	name: string;
	roleKey?: string;
	description?: string;
	isActive: boolean;
	departmentId?: number;
	department?: Department;
	permissions?: Permission[];
	users?: User[];
	createdAt: Date;
	updatedAt: Date;
}

export interface Permission {
	id: number;
	name: string;
	description?: string;
	type: string;
	action: string;
	isActive: boolean;
	roles?: Role[];
	createdAt: Date;
	updatedAt: Date;
}

// Branch & Department Types
export interface Branch {
	id: number;
	name: string;
	code: string;
	companyId?: number;
	latitude?: number | null;
	longitude?: number | null;
	maxOrderDistanceKm?: number;
	description?: string;
	address?: string;
	city?: string;
	country?: string;
	phone?: string;
	email?: string;
	managerId?: number;
	status: BranchStatus;
	isMain: boolean;
	users?: User[];
	products?: Product[];
	sales?: Sale[];
	customers?: Customer[];
	createdAt: Date;
	updatedAt: Date;
}

export enum BranchStatus {
	ACTIVE = "active",
	INACTIVE = "inactive",
}

export interface Department {
	id: number;
	name: string;
	description?: string;
	status: DepartmentStatus;
	parentDepartmentId?: number;
	parentDepartment?: Department;
	subDepartments?: Department[];
	roles?: Role[];
	createdAt: Date;
	updatedAt: Date;
}

export enum DepartmentStatus {
	ACTIVE = "active",
	INACTIVE = "inactive",
}

// Product Types
export interface Product {
	id: number;
	productNumber: string;
	name: string;
	description?: string;
	type: ProductType;
	unit: ProductUnit;
	status: ProductStatus;
	sellingPrice: number;
	purchasePrice: number;
	minSellingPrice?: number;
	taxRate: number;
	discountRate: number;
	discountValue: number;
	quantity: number;
	barcode?: string;
	sku?: string;
	image?: string;
	expiryDate?: Date;
	lowStockAlert?: number;
	trackQuantity: boolean;
	isWeightBased: boolean;
	weight?: number;
	categoryId?: number;
	branchId?: number;
	createdById?: number;
	category?: Category;
	branch?: Branch;
	createdBy?: User;
	saleItems?: SaleItem[];
	purchaseItems?: PurchaseItem[];
	createdAt: Date;
	updatedAt: Date;
}

export enum ProductType {
	PRODUCT = "product",
	SERVICE = "service",
	MENU_ITEM = "menu_item",
	COMBO = "combo",
}

export enum ProductUnit {
	PIECE = "piece",
	WEIGHT = "weight",
	BULK = "bulk",
}

export enum ProductStatus {
	ACTIVE = "active",
	INACTIVE = "inactive",
	OUT_OF_STOCK = "out_of_stock",
	DISCONTINUED = "discontinued",
}

export interface Category {
	id: number;
	name: string;
	description?: string;
	status: CategoryStatus;
	parentCategoryId?: number;
	parentCategory?: Category;
	subCategories?: Category[];
	products?: Product[];
	createdAt: Date;
	updatedAt: Date;
}

export enum CategoryStatus {
	ACTIVE = "active",
	INACTIVE = "inactive",
}

// Customer Types
export interface Customer {
	id: number;
	customerNumber: string;
	fullName?: string;
	firstName: string;
	lastName: string;
	email?: string;
	phone?: string;
	commercialRecord?: string;
	taxNumber?: string;
	address?: string;
	city?: string;
	country?: string;
	photo?: string;
	discountRate: number;
	discountValue: number;
	rewardPoints: number;
	notes?: string;
	status: CustomerStatus;
	joinDate?: Date;
	branchId?: number;
	branch?: Branch;
	sales?: Sale[];
	// Extended properties for UI display
	totalSales?: number;
	totalSpent?: number;
	lastPurchaseDate?: Date;
	createdAt: Date;
	updatedAt: Date;
}

export enum CustomerStatus {
	ACTIVE = "active",
	INACTIVE = "inactive",
	BLOCKED = "blocked",
}

// Sales Types
export type OrderSource = "admin" | "cashier" | "online_menu" | "third_party" | "api";

export interface Sale {
	id: number;
	invoiceNumber: string;
	status: SaleStatus;
	paymentStatus: PaymentStatus;
	paymentMethod: PaymentMethod;
	orderSource?: OrderSource;
	subtotal: number;
	taxAmount: number;
	discountAmount: number;
	shippingAmount: number;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	dueDate?: Date;
	notes?: string;
	reference?: string;
	customerId?: number;
	createdById: number;
	branchId?: number;
	customer?: Customer;
	createdBy?: User;
	branch?: Branch;
	items: SaleItem[];
	parentSaleId?: number | null;
	splitStatus?: string | null;
	splitChildren?: Sale[];
	payments?: Array<{
		id: number;
		amount: number;
		paymentMethod: string;
		paymentMethodId?: number | null;
		notes?: string | null;
	}>;
	createdAt: Date;
	updatedAt: Date;
}

export enum SaleStatus {
	PENDING = "pending",
	CONFIRMED = "confirmed",
	CANCELLED = "cancelled",
	REFUNDED = "refunded",
}

export enum PaymentStatus {
	PENDING = "pending",
	PARTIAL = "partial",
	PAID = "paid",
	OVERDUE = "overdue",
	REFUNDED = "refunded",
}

export enum PaymentMethod {
	CASH = "cash",
	CARD = "card",
	BANK_TRANSFER = "bank_transfer",
	CHECK = "check",
	CREDIT = "credit",
}

export interface SaleItem {
	id: number;
	quantity: number;
	unitPrice: number;
	discountRate: number;
	discountAmount: number;
	taxRate: number;
	taxAmount: number;
	totalAmount: number;
	notes?: string;
	saleId: number;
	productId: number;
	sale?: Sale;
	product?: Product;
	createdAt: Date;
	updatedAt: Date;
}

// Purchase Types
export interface Purchase {
	id: number;
	status: PurchaseStatus;
	paymentStatus: PaymentStatus;
	paymentMethod: PaymentMethod;
	subtotal: number;
	taxAmount: number;
	discountAmount: number;
	shippingAmount: number;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	expectedDeliveryDate?: Date;
	receivedDate?: Date;
	dueDate?: Date;
	notes?: string;
	reference?: string;
	supplierId?: number;
	createdById: number;
	branchId?: number;
	supplier?: Supplier;
	createdBy?: User;
	branch?: Branch;
	items: PurchaseItem[];
	createdAt: Date;
	updatedAt: Date;
}

export enum PurchaseStatus {
	PENDING = "pending",
	CONFIRMED = "confirmed",
	RECEIVED = "received",
	CANCELLED = "cancelled",
	REFUNDED = "refunded",
}

export interface PurchaseItem {
	id: number;
	quantity: number;
	unitPrice: number;
	discountRate: number;
	discountAmount: number;
	taxRate: number;
	taxAmount: number;
	totalAmount: number;
	notes?: string;
	purchaseId: number;
	productId: number;
	purchase?: Purchase;
	product?: Product;
	createdAt: Date;
	updatedAt: Date;
}

// Supplier Types
export interface Supplier {
	id: number;
	supplierNumber: string;
	name: string;
	email?: string;
	phone?: string;
	address?: string;
	city?: string;
	country?: string;
	contactPerson?: string;
	contactPhone?: string;
	taxNumber?: string;
	commercialRecord?: string;
	status: SupplierStatus;
	branchId?: number;
	branch?: Branch;
	purchases?: Purchase[];
	createdAt: Date;
	updatedAt: Date;
}

export enum SupplierStatus {
	ACTIVE = "active",
	INACTIVE = "inactive",
	BLOCKED = "blocked",
}

// Return Types
export interface Return {
	id: number;
	returnNumber: string;
	type: ReturnType;
	status: ReturnStatus;
	paymentStatus: ReturnPaymentStatus;
	reason?: string;
	notes?: string;
	subtotal: number;
	discountAmount: number;
	taxAmount: number;
	shippingAmount: number;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	returnDate?: Date;
	expectedRefundDate?: Date;
	refundDate?: Date;
	supplierId?: number;
	supplier?: Supplier;
	createdById: number;
	createdBy?: User;
	updatedById?: number;
	updatedBy?: User;
	items: ReturnItem[];
	createdAt: Date;
	updatedAt: Date;
}

export interface ReturnItem {
	id: number;
	quantity: number;
	unitPrice: number;
	discountPercentage?: number;
	discountAmount: number;
	taxPercentage?: number;
	taxAmount: number;
	totalAmount: number;
	notes?: string;
	returnId: number;
	return?: Return;
	productId: number;
	product?: Product;
	createdAt: Date;
	updatedAt: Date;
}

export enum ReturnStatus {
	PENDING = "PENDING",
	APPROVED = "APPROVED",
	REJECTED = "REJECTED",
	PROCESSED = "PROCESSED",
	CANCELLED = "CANCELLED",
}

export enum ReturnType {
	PURCHASE_RETURN = "PURCHASE_RETURN",
	SALE_RETURN = "SALE_RETURN",
}

export enum ReturnPaymentStatus {
	PENDING = "PENDING",
	PARTIAL = "PARTIAL",
	COMPLETED = "COMPLETED",
	REFUNDED = "REFUNDED",
}

// Expense Types
export interface Expense {
	id: number;
	description: string;
	amount: number;
	expenseDate: Date;
	reference?: string;
	notes?: string;
	paymentMethod?: ExpensePaymentMethod;
	categoryId: number;
	category: ExpenseCategory;
	createdById: number;
	createdBy?: User;
	updatedById?: number;
	updatedBy?: User;
	branchId?: number;
	branch?: Branch;
	createdAt: Date;
	updatedAt: Date;
}

export interface ExpenseCategory {
	id: number;
	name: string;
	description?: string;
	isActive: boolean;
	expenses?: Expense[];
	createdAt: Date;
	updatedAt: Date;
}

export enum ExpensePaymentMethod {
	CASH = "CASH",
	BANK_TRANSFER = "BANK_TRANSFER",
	CREDIT_CARD = "CREDIT_CARD",
	CHECK = "CHECK",
	ELECTRONIC_WALLET = "ELECTRONIC_WALLET",
}

// Tax Types
export interface Tax {
	id: number;
	name: string;
	description?: string;
	rate: number;
	code: string;
	type: TaxType;
	isActive: boolean;
	createdAt: Date;
	updatedAt: Date;
}

export enum TaxType {
	SALES = "sales",
	PURCHASE = "purchase",
	GENERAL = "general",
}

// Payment Method Types
export interface PaymentMethodEntity {
	id: number;
	name: string;
	description?: string;
	type: PaymentMethodType;
	bankName?: string;
	accountNumber?: string;
	accountName?: string;
	branch?: string;
	cardNumber?: string;
	cardType?: string;
	isDefault: boolean;
	isActive: boolean;
	createdAt: Date;
	updatedAt: Date;
}

export enum PaymentMethodType {
	BANK_ACCOUNT = "bank_account",
	CARD = "card",
	CASH = "cash",
	CHECK = "check",
}

// Company Types
export interface Company {
	id: number;
	name: string;
	logo?: string;
	taxNumber?: string;
	commercialRecord?: string;
	email?: string;
	phone?: string;
	address?: string;
	city?: string;
	country?: string;
	website?: string;
	currency?: string;
	timezone?: string;
	language?: string;
	isRTL: boolean;
	isWeightBased: boolean;
	weightUnit?: string;
	scaleStartIndicator?: string;
	weightDecimalPlaces: number;
	printWeightFromScale: boolean;
	createdAt: Date;
	updatedAt: Date;
}

// Dashboard & Reports Types
export interface DashboardSummary {
	salesSummary: SalesSummary;
	revenueSummary: RevenueSummary;
	productSummary: ProductSummary;
	customerSummary: CustomerSummary;
	topProducts: TopProduct[];
	lowStockProducts: Product[];
	purchasesSummary?: PurchasesSummary;
	suppliersSummary?: SuppliersSummary;
	employeesSummary?: EmployeesSummary;
}

export interface PurchasesSummary {
	totalPurchases: number;
	totalAmount: number;
	averagePurchaseValue: number;
}

export interface SuppliersSummary {
	totalSuppliers: number;
	totalPurchases: number;
	pendingOrders: number;
	topSuppliers?: Array<{
		id: number;
		name: string;
		totalOrders: number;
		totalAmount: number;
	}>;
}

export interface EmployeesSummary {
	totalEmployees: number;
	totalEmployeeSales: number;
	averagePerformance: number;
	topEmployees?: Array<{
		id: number;
		firstName: string;
		lastName: string;
		totalSales: number;
		totalAmount: number;
	}>;
}

export interface SalesSummary {
	totalSales: number;
	totalRevenue: number;
	totalItems: number;
	averageOrderValue: number;
	// Extended properties for UI display
	dailySales?: number;
	weeklySales?: number;
	monthlySales?: number;
	completedSales?: number;
	pendingSales?: number;
	cancelledSales?: number;
}

export interface RevenueSummary {
	totalRevenue: number;
	totalTax: number;
	totalDiscount: number;
	netRevenue: number;
}

export interface ProductSummary {
	totalProducts: number;
	activeProducts: number;
	lowStockProducts: number;
	outOfStockProducts: number;
	totalValue: number;
}

export interface CustomerSummary {
	totalCustomers: number;
	activeCustomers: number;
	newCustomersThisMonth: number;
	// Extended properties for UI display
	topCustomers?: Array<{
		id: number;
		firstName: string;
		lastName: string;
		totalSales: number;
		totalSpent: number;
	}>;
}

export interface TopProduct {
	id: number;
	productId: number;
	productName: string;
	productSku: string;
	totalQuantity: number;
	totalRevenue: number;
	averagePrice: number;
}

// Form Types
export interface LoginForm {
	id: number;
	password: string;
	rememberMe?: boolean;
}

export interface CreateProductForm {
	name: string;
	description?: string;
	type: ProductType;
	unit: ProductUnit;
	sellingPrice: number;
	purchasePrice: number;
	minSellingPrice?: number;
	taxRate?: number;
	discountRate?: number;
	discountValue?: number;
	quantity: number;
	barcode?: string;
	sku?: string;
	image?: string;
	expiryDate?: string;
	lowStockAlert?: number;
	trackQuantity?: boolean;
	isWeightBased?: boolean;
	weight?: number;
	categoryId?: number;
	branchId?: number;
}

export interface CreateCustomerForm {
	firstName: string;
	lastName: string;
	email?: string;
	phone?: string;
	address?: string;
	city?: string;
	country?: string;
	notes?: string;
	status?: CustomerStatus;
	branchId?: number;
}

export interface CreateSaleForm {
	customerId?: number;
	branchId?: number;
	paymentMethod: PaymentMethod;
	items: CreateSaleItemForm[];
	notes?: string;
}

export interface CreateSaleItemForm {
	productId: number;
	quantity: number;
	unitPrice: number;
	taxRate?: number;
	discountRate?: number;
}

// API Response Types
export interface ApiResponse<T = any> {
	data: T;
	message?: string;
	status: number;
}

export interface PaginatedResponse<T> {
	data: T[];
	total: number;
	page: number;
	limit: number;
}

// Filter Types
export interface ProductFilters {
	search?: string;
	categoryId?: number;
	branchId?: number;
	status?: ProductStatus;
	type?: ProductType;
	page?: number;
	limit?: number;
}

export interface SaleFilters {
	search?: string;
	branchId?: number;
	status?: SaleStatus;
	paymentStatus?: PaymentStatus;
	startDate?: string;
	endDate?: string;
	page?: number;
	limit?: number;
}

export interface CustomerFilters {
	search?: string;
	branchId?: number;
	status?: CustomerStatus;
	page?: number;
	limit?: number;
}

// POS Types
export interface Pos {
	id: number;
	name: string;
	code: string;
	status: PosStatus;
	serialNumber?: string;
	notes?: string;
	branchId?: number | null;
	branch?: Branch | null;
	companyId?: number | null;
	isMenuWeb?: boolean;
	offersTableService?: boolean;
	menuSlug?: string | null;
	employees?: User[];
	products?: Product[];
	createdAt: Date;
	updatedAt: Date;
}

export enum PosStatus {
	ACTIVE = "active",
	INACTIVE = "inactive",
}

export interface CreatePosDto {
	name: string;
	code?: string;
	status?: PosStatus;
	serialNumber?: string;
	notes?: string;
	branchId?: number | null;
	companyId?: number | null;
	isMenuWeb?: boolean;
	offersTableService?: boolean;
	menuSlug?: string | null;
	employeeIds?: number[];
	productIds?: number[];
}

export interface UpdatePosDto {
	name?: string;
	code?: string;
	status?: PosStatus;
	serialNumber?: string;
	notes?: string;
	branchId?: number | null;
	companyId?: number | null;
	isMenuWeb?: boolean;
	offersTableService?: boolean;
	menuSlug?: string | null;
	employeeIds?: number[];
	productIds?: number[];
}

export interface PosFilters {
	search?: string;
	branchId?: number;
	status?: PosStatus;
	dateFrom?: string;
	dateTo?: string;
	page?: number;
	limit?: number;
}
